<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader
      icon="el-icon-cpu"
      title="AI 报告配置"
      subtitle="控制前台《AI骨干人才保留建议报告》显示哪些模块"
    />

    <div class="config-card">
      <div class="section-title">报告包含模块</div>
      <div class="section-desc">勾选即显示，取消即隐藏。前台主管提交评估后看到的报告将据此展示。</div>

      <div class="module-list">
        <div
          v-for="m in modules"
          :key="m.key"
          class="module-item"
          :class="{ disabled: !m.enabled }"
          @click="toggleModule(m.key)"
        >
          <div class="module-checkbox">
            <i :class="m.enabled ? 'el-icon-check module-checked' : 'el-icon-close module-unchecked'" />
          </div>
          <div class="module-info">
            <div class="module-name">{{ m.label }}</div>
            <div class="module-desc">{{ m.desc }}</div>
          </div>
        </div>
      </div>

      <div class="section-actions">
        <el-button type="primary" class="btn-click" @click="saveConfig">
          <i class="el-icon-check" /> 保存配置
        </el-button>
        <el-button class="btn-click" @click="resetDefault">恢复默认</el-button>
      </div>
    </div>

    <div class="hint-card">
      <div class="hint-title"><i class="el-icon-info" /> 说明</div>
      <div class="hint-body">
        前台主管提交评估后看到的《AI骨干人才保留建议报告》将根据此处配置显示/隐藏对应模块。
        隐藏模块不影响后台知识库管理，只是不在报告中展示。
        当前 AI 多数配置写死在系统内部（模型参数、Prompt 模板、生成策略等），配置自由度较低。
        随着产品成熟、运营数据积累，将逐步下放更多配置项。
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '../../components/PageHeader.vue'

const DEFAULT_MODULES = {
  risk_analysis: true,
  behavior_detail: true,
  suggestions: true,
  cases: true,
  courses: true,
  talking_points: false
}

export default {
  name: 'AIReportConfig',
  components: { PageHeader },
  data() {
    return {
      modules: [
        { key: 'risk_analysis', label: '风险归因分析', desc: '分析员工离职风险的主要来源及权重', enabled: true },
        { key: 'behavior_detail', label: '命中行为明细', desc: '列出本次评估勾选的所有行为项及得分', enabled: true },
        { key: 'suggestions', label: '下一步管理建议', desc: '针对性的保留行动计划和具体措施', enabled: true },
        { key: 'cases', label: '过往成功案例', desc: '公司内部类似情况的成功保留案例', enabled: true },
        { key: 'courses', label: '推荐课程/资源', desc: '针对性的管理课程或学习资源推荐', enabled: true },
        { key: 'talking_points', label: '沟通话术参考', desc: '与员工沟通时的参考话术和FAQ', enabled: false }
      ]
    }
  },
  created() {
    this.loadConfig()
  },
  methods: {
    async loadConfig() {
      try {
        const taskId = this.$store.getters.currentTaskId
        const res = await this.$api.fetchAiReportModules(taskId ? { task_id: taskId } : {})
        if (res.data) {
          const data = res.data
          this.modules.forEach(m => {
            if (data[m.key] !== undefined) {
              m.enabled = !!data[m.key]
            }
          })
        }
      } catch (e) {
        console.warn('[AIReportConfig] 加载配置失败:', e.message)
      }
    },
    toggleModule(key) {
      const m = this.modules.find(x => x.key === key)
      if (m) m.enabled = !m.enabled
    },
    async saveConfig() {
      const modules = {}
      this.modules.forEach(m => { modules[m.key] = m.enabled })
      const taskId = this.$store.getters.currentTaskId
      try {
        await this.$api.updateAiReportModules({
          task_id: taskId || null,
          modules
        })
        this.$message.success('配置已保存')
      } catch (e) {
        this.$message.error('保存失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
      }
    },
    resetDefault() {
      this.$confirm('确定恢复默认配置？', '提示').then(() => {
        this.modules.forEach(m => {
          m.enabled = DEFAULT_MODULES[m.key]
        })
        this.$message.success('已恢复默认')
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.config-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-2xl);
  margin-bottom: var(--space-xl);
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: var(--space-sm);
}

.section-desc {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-xl);
}

.module-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  margin-bottom: var(--space-xl);
}

.module-item {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-lg);
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--duration-fast) var(--ease-out);
}

.module-item:hover {
  border-color: var(--color-primary);
}

.module-checkbox {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  flex-shrink: 0;
}

.module-checked {
  color: var(--color-primary);
  font-size: 18px;
  font-weight: bold;
}

.module-unchecked {
  color: var(--color-text-muted);
  font-size: 14px;
}

.module-info {
  flex: 1;
}

.module-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 2px;
}

.module-desc {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.module-item.disabled .module-name {
  color: var(--color-text-muted);
}

.module-item.disabled .module-desc {
  color: var(--color-text-muted);
}

.section-actions {
  display: flex;
  gap: var(--space-md);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--color-border-light);
}

.hint-card {
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-xl);
}

.hint-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-sm);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.hint-body {
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.8;
}
</style>
